import { useState, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { PencilSquareIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';
import DraggableRow from '../../components/common/DraggableRow';
import { clearMinimumOrderQuantities, createMinimumOrderQuantities, deleteMinimumOrderQuantities, getMinimumOrderQuantitiesById, MinimumOrderQuantitiesLoadingKeys } from '../../slices/moqSlice';

const MOQ = ({ 
  product,
  access,
  type
}) => {
  const dispatch = useDispatch();
  const {
    loadingKeys: moqLoadingKeys,
    items: moqItems,
    hasGroupItems
  } = useSelector((state) => state.moq);
 
  const [items, setItems] = useState([]);
  const [modifiedItems, setModifiedItems] = useState(null);
  const [editGroupItemsMode, setEditGroupItemsMode] = useState(false);

  useEffect(() => {
    ReactTooltip.rebuild()
    return () => {
      dispatch(clearMinimumOrderQuantities({}))
    }
  }, []);
  
  useEffect(() => {
    if (product?.id) {
      dispatch(getMinimumOrderQuantitiesById({ id: product.id, type, groupId: product.group_id }));
    }
  }, [product?.id, dispatch]);

  useEffect(() => {
    if (Array.isArray(moqItems)) {
      setItems([
        ...moqItems,
        { related_id: product?.id, quantity: 1, cost: 0, priority: moqItems.length + 1 }
      ]);
      setModifiedItems(null);
    }
  }, [moqItems, product?.id]);

  const handleUpdateItem = (index, updatedItem) => {
    if (access === "edit") {
      const itemsToModify = modifiedItems || items;
      const newItems = [...itemsToModify];
      newItems[index] = updatedItem;
      
      if (itemsToModify.length === index + 1) {
        newItems.push({ related_id: product?.id, quantity: 1, cost: 0, priority: itemsToModify.length + 1 });
      }
      
      setModifiedItems(newItems);
    }
  };

  const handleRemoveRow = async (itemId, itemIndex) => {
    if (access === "edit") {
      if (itemId) {
        dispatch(deleteMinimumOrderQuantities({ id: product.id, type, idToDelete: itemId, groupId: product.group_id }))
          .then(() => {
            toast.success("MOQ removed successfully.", { position: "bottom-right" });
          })
          .catch((error) => {
            toast.error(`Failed to remove MOQ: ${error.message}`, { position: "bottom-right" });
          });
      } else {
        const itemsToModify = modifiedItems || items;
        const updatedItems = itemsToModify.filter((_, index) => index !== itemIndex);
        setModifiedItems(updatedItems);
      }
    }
  };

  const moveRow = (fromIndex, toIndex) => {
    if (access === "edit") {
      const itemsToModify = modifiedItems || items;
      const newItems = [...itemsToModify];
      const [movedItem] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, movedItem);
      const updatedItems = newItems.map((item, index) => {
        if (index === newItems.length - 1 && !item.id && item.quantity === 1 && item.cost === 0) {
          return item;
        }
        return {
          ...item,
          priority: index + 1
        };
      });
      setModifiedItems(updatedItems);
    }
  };

  const handleSave = async () => {
    if (access === "edit") {
      if (modifiedItems) {
        dispatch(createMinimumOrderQuantities({
          creationData: {
            id: product?.id,
            type,
            groupId: product.group_id,
            newMoq: modifiedItems.filter((item) => item.quantity > 1 || item.cost)
          }
        })).then(() => {
          toast.success("MOQ saved successfully.", { position: "bottom-right" });
          setEditGroupItemsMode(false);
          setModifiedItems(null); // Reset modified items after save
        }).catch((error) => {
          toast.error(`Failed to save MOQ: ${error.message}`, { position: "bottom-right" });
        });
      }
    }
  };

  const dataIsLoading = () => 
    moqLoadingKeys.includes(MinimumOrderQuantitiesLoadingKeys.getMOQ) ||
    moqLoadingKeys.includes(MinimumOrderQuantitiesLoadingKeys.createMOQ) ||
    moqLoadingKeys.includes(MinimumOrderQuantitiesLoadingKeys.deleteMOQ);

  const displayItems = modifiedItems || items;
  const disableItems = !editGroupItemsMode && hasGroupItems;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`mt-6`}>
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-row gap-2 items-center">
            <h3 className="text-lg font-medium text-gray-900">Minimum Order Quantities</h3>
            {type === "Product" && hasGroupItems && (
              <>
                <ShieldExclamationIcon 
                  className="h-7 w-7 text-blue-700" 
                  data-for={`${type}_tooltip`}
                  data-tip={true}
                />
                <ReactTooltip 
                  id={`${type}_tooltip`} 
                  html={true}
                  effect='solid'
                >
                  {`
                    <div class="w-72 text-center py-1 px-1">
                      The product variant has product group items.
                    </div>
                  `}
                </ReactTooltip>
              </>
            )}
          </div>
          {type === "Product" && hasGroupItems && access === "edit" && (
            <button
              className="flex flex-row gap-2 items-center bg-blue-500 text-white py-0.5 px-2 rounded hover:bg-blue-700"
              onClick={() => {
                setEditGroupItemsMode(true);
                setModifiedItems(displayItems.map((item) => {
                  const { isGroupItem, id, ...anyData } = item
                  return {
                    ...anyData,
                    related_id: product?.id
                  }
                }))
              }}
            >
              <span>Edit</span>
              <PencilSquareIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-x-auto relative">
          {dataIsLoading() && <OverlayGrayBackground />}
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[45%]" />
              <col className="w-[45%]" />
              {access === "edit" && (
                <col className="w-[5%]" />
              )}
            </colgroup>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="py-3 px-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="py-3 px-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                {access === "edit" && (
                  <th className="py-3 px-1 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sr-only"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {displayItems.map((item, index) => (
                <DraggableRow
                  key={`${item.id}_${index}`}
                  rowType="moq"
                  id={item.id || index}
                  index={index}
                  item={item}
                  access={access}
                  moveRow={moveRow}
                  handleUpdateItem={handleUpdateItem}
                  handleRemoveRow={handleRemoveRow}
                  dataIsLoading={dataIsLoading()}
                  isLastElement={displayItems.length === index + 1}
                  disableItems={disableItems}
                />
              ))}
            </tbody>
          </table>
        </div>
        {access === "edit" && (
          <div className="mt-3 flex justify-end space-x-3">
            <button
              onClick={handleSave}
              disabled={!modifiedItems}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white ${
                modifiedItems ? 'bg-primary hover:bg-gray-700' : 'bg-gray-400 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700`}
              aria-label="Save changes"
            >
              <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save
            </button>
          </div>
        )}
      </div>
    </DndProvider>
  );
};

export default MOQ; 